// Iteration 108 -- Team announcements section on event detail.
// Shows the latest publication per participating team. Collapsed by default
// when 3+ teams have published. Read-only -- publishing is on the team page.

import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";
import type { TeamPublicationPublic } from "@dugads/shared";
import * as api from "@/lib/api";

interface Props {
  eventId: string;
}

const formatPublishedAt = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function TeamAnnouncementsSection({ eventId }: Props) {
  const [publications, setPublications] = useState<TeamPublicationPublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .getEventPublications(eventId)
      .then((res) => {
        if (cancelled) return;
        setPublications(res.publications);
      })
      .catch((e) => {
        if (cancelled) return;
        // 403 here just means the caller doesn't have access -- silently hide
        // the section. Any other error surfaces so we don't hide bugs.
        if (e instanceof api.ApiError && e.status === 403) {
          setPublications([]);
        } else {
          setError(e instanceof api.ApiError ? e.message : "Failed to load announcements");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (publications === null && !error) return null;
  if (error) {
    return (
      <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-region="team-announcements" data-state="error">
        Could not load team announcements: {error}
      </div>
    );
  }
  if (!publications || publications.length === 0) return null;

  const collapsedByDefault = publications.length >= 3;
  const effectivelyOpen = collapsedByDefault ? open : true;

  return (
    <div className="mt-4 rounded-lg border border-border" data-region="team-announcements" data-count={publications.length}>
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 pt-4 pb-2 text-left"
        onClick={() => collapsedByDefault && setOpen((v) => !v)}
        disabled={!collapsedByDefault}
      >
        <div className="flex items-center gap-2">
          {collapsedByDefault && (effectivelyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
          <Megaphone className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-medium">Team announcements ({publications.length})</h2>
        </div>
      </button>
      {effectivelyOpen && (
        <ul className="flex flex-col gap-3 px-4 pb-4">
          {publications.map((p) => (
            <li key={p.id} className="rounded-md border border-border bg-card px-4 py-3" data-team-id={p.team_id}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <Link
                  to={`/teams/${p.team_slug}`}
                  className="text-sm font-semibold text-foreground hover:underline"
                >
                  {p.team_name}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {p.published_by_name} &middot; {formatPublishedAt(p.published_at)}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-foreground" data-content>
                {p.content}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
